import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import {
  Kcc20AccountPosition,
  Kcc20AddressOrdersQuery,
  Kcc20Balance,
  Kcc20Order,
  Kcc20TradingApiService,
} from '../trading/kcc20-trading-api.service';
import {
  Kcc20DeployedTokensPage,
  Kcc20DeployedTokensQuery,
  Kcc20TokenApiService,
  Kcc20TokenDetail,
} from '../tokens/kcc20-token-api.service';
import {
  Kcc20PortfolioAssetRow,
  Kcc20WalletOperationSubmissionApiService,
} from '../wallet-operations/kcc20-wallet-operation-submission-api.service';

export interface Kcc20HoldingsQuery {
  deployedTokens?: Kcc20DeployedTokensQuery;
  openOrders?: Kcc20AddressOrdersQuery;
}

export interface Kcc20HoldingsSection<T> {
  data: T;
  error?: string;
}

export interface Kcc20HoldingsSnapshot {
  balances: Kcc20HoldingsSection<Kcc20Balance[]>;
  positions: Kcc20HoldingsSection<Kcc20AccountPosition[]>;
  openOrders: Kcc20HoldingsSection<Kcc20Order[]>;
  deployedTokens: Kcc20HoldingsSection<Kcc20DeployedTokensPage>;
  warnings: string[];
}

export interface Kcc20TokenHoldingsSnapshot {
  balance: Kcc20HoldingsSection<Kcc20Balance | undefined>;
  position: Kcc20HoldingsSection<Kcc20AccountPosition | undefined>;
  warnings: string[];
}

@Injectable({ providedIn: 'root' })
export class Kcc20HoldingsService {
  private readonly tradingApi = inject(Kcc20TradingApiService);
  private readonly tokenApi = inject(Kcc20TokenApiService);
  private readonly walletOperationsApi = inject(
    Kcc20WalletOperationSubmissionApiService,
  );

  loadHoldings(
    ownerIdentifier: string,
    query: Kcc20HoldingsQuery = {},
  ): Observable<Kcc20HoldingsSnapshot> {
    const deployedQuery = {
      sortBy: 'ticker',
      sortDirection: 'asc',
      ...query.deployedTokens,
      limit: 10,
      offset: query.deployedTokens?.offset ?? 0,
    } satisfies Kcc20DeployedTokensQuery;
    const openOrdersQuery = {
      status: 'open',
      ...query.openOrders,
      limit: 10,
      offset: query.openOrders?.offset ?? 0,
    } satisfies Kcc20AddressOrdersQuery;

    return forkJoin({
      balances: this.section(
        this.tradingApi.getOwnerBalances(ownerIdentifier),
        [],
        'Balances',
      ),
      positions: this.section(
        this.tradingApi.getOwnerPositions(ownerIdentifier),
        [],
        'Positions',
      ),
      openOrders: this.section(
        this.tradingApi
          .getOwnerOrders(ownerIdentifier, openOrdersQuery)
          .pipe(map((response) => response.items)),
        [],
        'Open orders',
      ),
      deployedTokens: this.section(
        this.tokenApi.getDeployedTokens(ownerIdentifier, deployedQuery),
        this.emptyDeployedTokensPage(deployedQuery),
        'Created tokens',
      ),
    }).pipe(
      map((snapshot) => ({
        ...snapshot,
        warnings: this.warnings(snapshot),
      })),
    );
  }

  loadPositions(
    ownerIdentifier: string,
  ): Observable<Kcc20HoldingsSection<Kcc20AccountPosition[]>> {
    return this.section(
      this.tradingApi.getOwnerPositions(ownerIdentifier),
      [],
      'Positions',
    );
  }

  loadTokenHoldings(
    ownerIdentifier: string,
    canonicalTokenId: string,
    marketId?: string | null,
  ): Observable<Kcc20TokenHoldingsSnapshot> {
    const normalizedCanonicalTokenId = canonicalTokenId.toLowerCase();
    const normalizedMarketId = marketId?.toLowerCase();

    return this.walletOperationsApi
      .getPortfolioTokenBalance(normalizedCanonicalTokenId)
      .pipe(
        map((row) => ({
          balance: {
            data: this.rowToBalance(row, ownerIdentifier),
          },
          position: {
            data: this.rowToPosition(row),
          },
          warnings: [],
        })),
        catchError(() =>
          this.loadLegacyTokenHoldings(
            ownerIdentifier,
            normalizedCanonicalTokenId,
            normalizedMarketId,
          ),
        ),
      );
  }

  private loadLegacyTokenHoldings(
    ownerIdentifier: string,
    normalizedCanonicalTokenId: string,
    normalizedMarketId?: string,
  ): Observable<Kcc20TokenHoldingsSnapshot> {
    return forkJoin({
      balances: this.section(
        this.tradingApi.getOwnerBalances(ownerIdentifier),
        [],
        'Balances',
      ),
      positions: this.section(
        this.tradingApi.getOwnerPositions(ownerIdentifier),
        [],
        'Positions',
      ),
    }).pipe(
      map(({ balances, positions }) => ({
        balance: {
          data: balances.data.find(
            (balance) =>
              balance.tokenIdHex.toLowerCase() === normalizedCanonicalTokenId &&
              !balance.isMinter,
          ),
          error: balances.error,
        },
        position: {
          data: this.findPosition(
            positions.data,
            normalizedCanonicalTokenId,
            normalizedMarketId,
          ),
          error: positions.error,
        },
        warnings: [balances.error, positions.error].filter(
          (message): message is string => !!message,
        ),
      })),
    );
  }

  private rowToBalance(
    row: Kcc20PortfolioAssetRow,
    ownerIdentifier: string,
  ): Kcc20Balance | undefined {
    if (!this.isPositiveAmount(row.nativeBalance) && row.assetUtxoCount <= 0) {
      return undefined;
    }

    return {
      tokenIdHex: row.canonicalTokenIdHex,
      ownerIdentifier,
      amount: row.nativeBalance,
      identifierType: 0,
      identifierTypeName: 'pubkey',
      isMinter: false,
      utxoCount: row.assetUtxoCount,
      lastChangeMs: row.lastChangeMs ?? 0,
    };
  }

  private rowToPosition(
    row: Kcc20PortfolioAssetRow,
  ): Kcc20AccountPosition | undefined {
    const hasPosition =
      !!row.wrappedMarketIdHex ||
      this.isPositiveAmount(row.orderbookBalance) ||
      row.openOrderCount > 0;
    if (!hasPosition) {
      return undefined;
    }

    return {
      canonicalTokenIdHex: row.canonicalTokenIdHex,
      wrapperIdHex: row.wrapperIdHex,
      wrappedMarketIdHex: row.wrappedMarketIdHex,
      canonicalBalance: row.nativeBalance,
      wrappedBalance: row.orderbookBalance,
      nativeBalance: row.nativeBalance,
      orderbookBalance: row.orderbookBalance,
      totalBalance: row.totalBalance,
      openAskAmount: row.openAskAmount,
      openBidAmount: row.openBidAmount,
      openOrderCount: row.openOrderCount,
      feeTicketSupported: row.feeTicketSupported,
      lastChangeMs: row.lastChangeMs,
    };
  }

  findPosition(
    positions: Kcc20AccountPosition[],
    canonicalTokenId: string,
    marketId?: string,
  ): Kcc20AccountPosition | undefined {
    const normalizedCanonicalTokenId = canonicalTokenId.toLowerCase();
    const normalizedMarketId = marketId?.toLowerCase();

    return positions.find(
      (position) =>
        position.canonicalTokenIdHex.toLowerCase() ===
          normalizedCanonicalTokenId ||
        (!!normalizedMarketId &&
          position.wrapperIdHex?.toLowerCase() === normalizedMarketId) ||
        (!!normalizedMarketId &&
          position.wrappedMarketIdHex?.toLowerCase() === normalizedMarketId),
    );
  }

  private section<T>(
    request: Observable<T>,
    fallback: T,
    label: string,
  ): Observable<Kcc20HoldingsSection<T>> {
    return request.pipe(
      map((data) => ({ data })),
      catchError(() =>
        of({
          data: fallback,
          error: `${label} could not be loaded. Refresh again in a few seconds.`,
        }),
      ),
    );
  }

  private warnings(snapshot: {
    balances: Kcc20HoldingsSection<unknown>;
    positions: Kcc20HoldingsSection<unknown>;
    openOrders: Kcc20HoldingsSection<unknown>;
    deployedTokens: Kcc20HoldingsSection<unknown>;
  }): string[] {
    return [
      snapshot.balances.error,
      snapshot.positions.error,
      snapshot.openOrders.error,
      snapshot.deployedTokens.error,
    ].filter((message): message is string => !!message);
  }

  private isPositiveAmount(value: string | null | undefined): boolean {
    if (!value) return false;
    if (/^(0|[1-9]\d*)$/.test(value)) {
      return BigInt(value) > 0n;
    }
    return Number(value) > 0;
  }

  private emptyDeployedTokensPage(
    query: Kcc20DeployedTokensQuery,
  ): Kcc20DeployedTokensPage {
    return {
      items: [] as Kcc20TokenDetail[],
      total: 0,
      limit: 10,
      offset: query.offset ?? 0,
      sortBy: query.sortBy,
      sortDirection: query.sortDirection,
    };
  }
}
